/**
 * Types pour la gestion des Clients et Cahiers des Charges
 */

// ==================== CLIENT ====================

export interface Client {
  id: string;
  companyName: string;
  logo?: string; // Base64 ou URL de l'image
  brandPrimaryColor?: string;
  brandSecondaryColor?: string;
  address: string;
  phone: string;
  email: string;
  contactPerson: string;
  contactRole?: string;
  sector: string; // Secteur d'activité
  siret?: string;
  website?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ClientFormData extends Omit<Client, 'id' | 'createdAt' | 'updatedAt'> {
  logoFile?: File; // Pour l'upload de fichier
}

// ==================== CAHIER DES CHARGES ====================

export type SpecificationStatus =
  | 'draft'       // Brouillon
  | 'in_review'   // En révision
  | 'approved'    // Approuvé
  | 'signed'      // Signé
  | 'archived';   // Archivé

export type SpecificationPriority = 'low' | 'medium' | 'high' | 'critical';

export interface SpecificationSection {
  id: string;
  title: string;
  content: string;
  order: number;
  subsections?: SpecificationSubsection[];
}

export interface SpecificationSubsection {
  id: string;
  title: string;
  content: string;
  order: number;
}

export interface FunctionalRequirement {
  id: string;
  code: string; // Ex: "REQ-FUNC-001"
  title: string;
  description: string;
  priority: SpecificationPriority;
  category: string; // Ex: "Authentification", "Paiement", etc.
  acceptanceCriteria: string[];
  estimatedComplexity?: 'low' | 'medium' | 'high';
}

export interface TechnicalRequirement {
  id: string;
  code: string; // Ex: "REQ-TECH-001"
  title: string;
  description: string;
  category: string; // Ex: "Sécurité", "Performance", etc.
  mandatory: boolean;
}

export type NonFunctionalCategory =
  | 'performance'
  | 'security'
  | 'usability'
  | 'reliability'
  | 'scalability'
  | 'maintainability'
  | 'portability'
  | 'compliance'
  | 'other';

export interface NonFunctionalRequirement {
  id: string;
  code: string; // Ex: "REQ-NF-001"
  title: string;
  description: string;
  category: NonFunctionalCategory;
  priority: SpecificationPriority;
  metric?: string; // Ex: "Temps de réponse < 200ms"
  acceptanceCriteria: string[];
}

export interface Deliverable {
  id: string;
  name: string;
  description: string;
  dueDate?: Date;
  responsible?: string;
}

export interface ProjectConstraint {
  id: string;
  type: 'budget' | 'time' | 'resource' | 'technical' | 'legal' | 'other';
  description: string;
  impact: 'low' | 'medium' | 'high';
}

export interface Specification {
  id: string;
  clientId: string; // Référence au client
  projectId?: string; // Référence au projet (optionnel)
  title: string;
  reference: string; // Numéro de référence unique (ex: "CDC-2024-001")
  version: string; // Ex: "1.0", "1.1", etc.
  status: SpecificationStatus;

  // Contexte du projet
  projectContext: string;
  projectObjectives: string;
  projectScope: string;

  // Structure du document
  sections: SpecificationSection[];

  // Exigences
  functionalRequirements: FunctionalRequirement[];
  technicalRequirements: TechnicalRequirement[];
  nonFunctionalRequirements: NonFunctionalRequirement[];

  // Contraintes et livrables
  constraints: ProjectConstraint[];
  deliverables: Deliverable[];

  // Planning
  estimatedStartDate?: Date;
  estimatedEndDate?: Date;
  estimatedBudget?: number;
  currency?: string; // Ex: "EUR", "USD"

  // Parties prenantes
  stakeholders: {
    name: string;
    role: string;
    contact?: string;
  }[];

  // Métadonnées
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  approvedBy?: string;
  approvedAt?: Date;
  signedBy?: string;
  signedAt?: Date;

  // Notes et pièces jointes
  notes?: string;
  attachments?: {
    id: string;
    name: string;
    url: string;
    type: string;
    size: number;
  }[];
}

// ==================== TEMPLATES ====================

export interface SpecificationTemplate {
  id: string;
  name: string;
  description: string;
  category: string; // Ex: "Web", "Mobile", "Desktop", "Infrastructure"
  sections: Omit<SpecificationSection, 'id'>[];
  defaultFunctionalRequirements?: Omit<FunctionalRequirement, 'id'>[];
  defaultTechnicalRequirements?: Omit<TechnicalRequirement, 'id'>[];
  defaultNonFunctionalRequirements?: Omit<NonFunctionalRequirement, 'id'>[];
}

// ==================== PDF GENERATION CONFIG ====================

export interface PDFGenerationConfig {
  includeTableOfContents: boolean;
  includeCoverPage: boolean;
  includeClientLogo: boolean;
  includeCompanyLogo: boolean;
  includeSignatureSection: boolean;
  includeApprovalSection: boolean;
  includeVersionHistory: boolean;
  pageNumbering: boolean;
  headerText?: string;
  footerText?: string;
  primaryColor?: string; // Couleur principale du document
  secondaryColor?: string;
}

export const DEFAULT_PDF_CONFIG: PDFGenerationConfig = {
  includeTableOfContents: true,
  includeCoverPage: true,
  includeClientLogo: true,
  includeCompanyLogo: false,
  includeSignatureSection: true,
  includeApprovalSection: true,
  includeVersionHistory: true,
  pageNumbering: true,
  primaryColor: '#0891b2', // cyan-600
  secondaryColor: '#6366f1', // indigo-500
};

// ==================== CONFIGURATION ====================

export interface SpecificationConfig {
  autoSaveDraft: boolean;
  autoSaveInterval: number; // en secondes
  defaultCurrency: string;
  defaultLanguage: 'fr' | 'en';
  requireApprovalBeforePDF: boolean;
  templates: SpecificationTemplate[];
  pdfConfig: PDFGenerationConfig;
}

export const DEFAULT_SPECIFICATION_CONFIG: SpecificationConfig = {
  autoSaveDraft: true,
  autoSaveInterval: 60,
  defaultCurrency: 'EUR',
  defaultLanguage: 'fr',
  requireApprovalBeforePDF: false,
  templates: [],
  pdfConfig: DEFAULT_PDF_CONFIG,
};

// ==================== STATISTICS ====================

export interface SpecificationStatistics {
  totalClients: number;
  totalSpecifications: number;
  specificationsByStatus: Record<SpecificationStatus, number>;
  averageRequirementsPerSpec: number;
  totalFunctionalRequirements: number;
  totalTechnicalRequirements: number;
  totalNonFunctionalRequirements: number;
  recentSpecifications: Specification[];
}

// ==================== HELPERS ====================

export const SPECIFICATION_STATUS_LABELS: Record<SpecificationStatus, string> = {
  draft: 'Brouillon',
  in_review: 'En révision',
  approved: 'Approuvé',
  signed: 'Signé',
  archived: 'Archivé',
};

export const SPECIFICATION_STATUS_COLORS: Record<SpecificationStatus, string> = {
  draft: '#6b7280', // gray-500
  in_review: '#f59e0b', // amber-500
  approved: '#10b981', // emerald-500
  signed: '#0891b2', // cyan-600
  archived: '#64748b', // slate-500
};

export const PRIORITY_LABELS: Record<SpecificationPriority, string> = {
  low: 'Faible',
  medium: 'Moyenne',
  high: 'Élevée',
  critical: 'Critique',
};

export const PRIORITY_COLORS: Record<SpecificationPriority, string> = {
  low: '#10b981', // emerald-500
  medium: '#f59e0b', // amber-500
  high: '#f97316', // orange-500
  critical: '#dc2626', // red-600
};

export const NON_FUNCTIONAL_CATEGORY_LABELS: Record<NonFunctionalCategory, string> = {
  performance: 'Performance',
  security: 'Sécurité',
  usability: 'Utilisabilité',
  reliability: 'Fiabilité',
  scalability: 'Scalabilité',
  maintainability: 'Maintenabilité',
  portability: 'Portabilité',
  compliance: 'Conformité',
  other: 'Autre',
};

export const CONSTRAINT_TYPE_LABELS: Record<ProjectConstraint['type'], string> = {
  budget: 'Budget',
  time: 'Délai',
  resource: 'Ressource',
  technical: 'Technique',
  legal: 'Légal',
  other: 'Autre',
};
